import { getEcState } from '@packages/utilities';
import type { GetServerSidePropsContext } from 'next';

import type { ParsedUrlQuery } from 'querystring';
import { getMainAudience, decodeFromBase64 } from '../../../utils';
import { fetchPromoData } from './fetchPromoData';
import { fetchContentSnippetData } from './fetchContentSnippetData';
import { doTheAbTest } from '../ab-test/abTest';
/**
 * fetch, filter and cleanup promotion data
 *
 * @param query holds different filter options by query params
 * @param cookies holds cookies to also filter information on
 * @param context
 * @param locale defines the language for the returned data
 * @param callOwnApi
 * @returns data for shop (by page context query param),
 * selected (by query param BonusNumber) and
 * active (by cookie value) promotions
 */
export const promotionBannerDataFetcher = async (
  query: {
    shopId?: string;
    BonusNumber?: string;
    P1?: string;
    TestDate?: string;
    ParentCode?: string;
    platform?: 'app' | 'web';
    AffiliateID?: string;
  },
  cookies: Partial<{
    [key: string]: string;
  }>,
  context?: GetServerSidePropsContext<ParsedUrlQuery> | undefined,
  locale?: string,
  callOwnApi?: boolean,
) => {
  const {
    shopId = '-1',
    BonusNumber,
    P1,
    TestDate: testDate,
    ParentCode,
    platform,
    AffiliateID,
  } = query;
  const audience = getMainAudience(getEcState(cookies));
  const abTestData = await doTheAbTest({
    BonusNumber,
    AffiliateID,
    cookies,
    context,
    locale,
    testDate,
    audience,
    platform,
    callOwnApi,
  });
  let { fetchedShopPromotionData } = abTestData;
  const { mvtTreatment } = abTestData;
  if (!mvtTreatment) {
    fetchedShopPromotionData = await fetchContentSnippetData(
      {
        shopId,
        locale,
        testDate,
        audience,
        platform,
      },
      callOwnApi,
    );
  }
  const selectedBonusNumber = BonusNumber || (P1 && decodeFromBase64(P1)) || undefined;
  let fetchedSelectedPromotionData;
  try {
    fetchedSelectedPromotionData =
      selectedBonusNumber &&
      (await fetchPromoData(
        { cart: selectedBonusNumber, texts: ParentCode || selectedBonusNumber },
        locale,
        callOwnApi,
        testDate,
      ));
  } catch {
    // do nothing - leave data as undefined
  }

  let fetchedActivePromotionData;
  const cookieCode = cookies.bonuscode || cookies.loyaltyCode;
  try {
    fetchedActivePromotionData =
      cookieCode &&
      (await fetchPromoData(
        { cart: cookieCode, texts: cookies.textsBonusCode || cookieCode },
        locale,
        callOwnApi,
        testDate,
      ));
  } catch {
    // do nothing - leave data as undefined
  }
  if (mvtTreatment === 'test') {
    fetchedShopPromotionData = undefined;
  }
  // I need to omit the complete prop if there is no value to prevent error: "'undefined' cannot be serialized as JSON"
  return {
    ...(fetchedShopPromotionData?.[0] && { shop: fetchedShopPromotionData?.[0] }),
    ...(fetchedSelectedPromotionData && { selected: fetchedSelectedPromotionData }),
    ...(fetchedActivePromotionData && { active: fetchedActivePromotionData }),
  };
};
